import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Check, MonitorPlay } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getUpgradeOptions, formatCapShort } from '../lib/planLimits';

const ADVANCE_BUFFER_MS = 500;   // wait after a phase finishes before switching
const SAFETY_CAP_MS = 11000;     // hard ceiling per phase in case onDone never fires

const ORBS = [
  { left:'6%',  top:'14%', w:280, color:'rgba(30,27,75,0.14)',   dur:13, dx:48,  dy:-40 },
  { left:'76%', top:'62%', w:240, color:'rgba(245,158,11,0.09)', dur:15, dx:-46, dy:42  },
  { left:'42%', top:'34%', w:340, color:'rgba(30,27,75,0.07)',   dur:18, dx:30,  dy:-48 },
  { left:'84%', top:'12%', w:180, color:'rgba(245,158,11,0.08)', dur:11, dx:-50, dy:34  },
  { left:'16%', top:'78%', w:210, color:'rgba(76,29,149,0.08)',  dur:16, dx:34,  dy:-28 },
];
const BackgroundOrbs = () => (
  <div style={{ position:'absolute',inset:0,overflow:'hidden',pointerEvents:'none',zIndex:0 }}>
    {ORBS.map((o,i) => (
      <motion.div key={i}
        animate={{ x:[0,o.dx,-o.dx*0.4,0], y:[0,o.dy,-o.dy*0.3,0] }}
        transition={{ duration:o.dur,repeat:Infinity,ease:'easeInOut',repeatType:'mirror' }}
        style={{ position:'absolute',left:o.left,top:o.top,width:o.w,height:o.w,
          marginLeft:-o.w/2,marginTop:-o.w/2,borderRadius:'50%',
          background:o.color,filter:'blur(70px)' }}
      />
    ))}
  </div>
);

function useTypewriter(fullText, { startDelay=0, charInterval=14, active=true }={}) {
  const [displayedText, setDisplayedText] = useState('');
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!active) { setDisplayedText(''); setDone(false); return; }
    setDisplayedText(''); setDone(false);
    let charIdx=0, iv;
    const startT = setTimeout(() => {
      iv = setInterval(() => {
        charIdx++;
        setDisplayedText(fullText.slice(0,charIdx));
        if (charIdx>=fullText.length) { clearInterval(iv); setDone(true); }
      }, charInterval);
    }, startDelay);
    return () => { clearTimeout(startT); if(iv) clearInterval(iv); };
  }, [fullText, startDelay, charInterval, active]);
  return { displayedText, done };
}

const BlinkingCursor = ({ color='#1E1B4B' }) => (
  <motion.span
    animate={{ opacity:[1,0,1] }}
    transition={{ duration:0.85, repeat:Infinity, ease:'steps(1)' }}
    style={{ display:'inline-block',width:1.5,height:'1em',background:color,
      marginLeft:2,verticalAlign:'text-bottom' }}
  />
);

/* ── 16:9 slide shell ────────────────────────────────────────────────────── */
const MockSlide = ({ children, width=560 }) => (
  <div style={{
    width, aspectRatio:'16/9', flexShrink:0,
    background:'#ffffff', borderRadius:12,
    border:'3px solid #1E1B4B',
    boxShadow:'0 32px 80px rgba(0,0,0,0.50), 0 2px 0 rgba(245,158,11,0.35)',
    padding:5, overflow:'hidden',
  }}>
    <div style={{ height:'100%', border:'1.5px solid #F59E0B', borderRadius:8,
      display:'flex', flexDirection:'column', overflow:'hidden' }}>
      {children}
    </div>
  </div>
);

const SlideHeaderBar = ({ label, slideNum }) => (
  <div style={{ background:'#1E1B4B', padding:'7px 14px',
    display:'flex', alignItems:'center', gap:7, flexShrink:0 }}>
    <picture>
      <source srcSet="/TechGenieMascot.webp" type="image/webp" />
      <img src="/TechGenieMascot.png" alt=""
        style={{ width:16,height:16,objectFit:'contain' }}/>
    </picture>
    <span style={{ fontSize:9, fontWeight:800, color:'white', letterSpacing:'0.4px' }}>
      TeachGenie.ai
    </span>
    {label && (
      <span style={{ marginLeft:'auto', fontSize:7.5, fontWeight:700,
        color:'rgba(255,255,255,0.9)',
        background:'rgba(245,158,11,0.28)', padding:'2px 7px', borderRadius:99 }}>
        {label}
      </span>
    )}
    {slideNum && (
      <span style={{ fontSize:7, fontWeight:600, color:'rgba(255,255,255,0.5)',
        marginLeft: label ? 5 : 'auto' }}>
        slide {slideNum}
      </span>
    )}
  </div>
);

const SlideFooterBar = ({ text }) => (
  <div style={{ background:'linear-gradient(90deg,#1E1B4B,#4c1d95)', padding:'5px 14px',
    marginTop:'auto', flexShrink:0,
    display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
    <picture>
      <source srcSet="/TechGenieMascot.webp" type="image/webp" />
      <img src="/TechGenieMascot.png" alt=""
        style={{ width:9,height:9,objectFit:'contain' }}/>
    </picture>
    <span style={{ fontSize:6.8, color:'rgba(255,255,255,0.9)', fontWeight:700, letterSpacing:'0.2px' }}>
      Powered by TeachGenie™.ai{text ? ` · ${text}` : ''}
    </span>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 1 — TITLE SLIDE
═══════════════════════════════════════════════════════════════════════════ */
const PhaseCover = ({ onDone = () => {} }) => {
  const [lineIn,   setLineIn]   = useState(false);
  const [metaIn,   setMetaIn]   = useState(false);
  const [badgesIn, setBadgesIn] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setLineIn(true),   550);
    const t2 = setTimeout(() => setMetaIn(true),    850);
    const t3 = setTimeout(() => setBadgesIn(true), 1150);
    const t4 = setTimeout(onDone, 2000);
    return () => [t1,t2,t3,t4].forEach(clearTimeout);
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ y:90, opacity:0, scale:0.82, rotateX:18 }}
        animate={{ y:0, opacity:1, scale:1, rotateX:0 }}
        transition={{ duration:0.75, ease:[0.22,1,0.36,1] }}
        style={{ transformOrigin:'bottom center' }}>
        <MockSlide>
          <div style={{ height:5, background:'linear-gradient(90deg,#F59E0B,#fbbf24,#F59E0B)',
            flexShrink:0 }}/>
          <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
            justifyContent:'center', gap:11, padding:'10px 26px', textAlign:'center',
            position:'relative' }}>
            <div style={{ position:'absolute', inset:0, opacity:0.04,
              backgroundImage:'radial-gradient(#1E1B4B 1px, transparent 1px)',
              backgroundSize:'18px 18px' }}/>

            <motion.div
              initial={{ scale:0, rotate:-20 }} animate={{ scale:1, rotate:0 }}
              transition={{ type:'spring', stiffness:300, damping:14, delay:0.2 }}
              style={{ width:50, height:50, borderRadius:14,
                background:'linear-gradient(135deg,#1E1B4B,#4c1d95)',
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 14px 36px rgba(30,27,75,0.45)', zIndex:1 }}>
              <picture>
                <source srcSet="/TechGenieMascot.webp" type="image/webp" />
                <img src="/TechGenieMascot.png" alt=""
                  style={{ width:34,height:34,objectFit:'contain' }}/>
              </picture>
            </motion.div>

            <div style={{ zIndex:1 }}>
              <motion.div
                initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
                transition={{ delay:0.32, duration:0.45 }}
                style={{ fontSize:7.5, fontWeight:700, color:'#F59E0B',
                  letterSpacing:'2.2px', textTransform:'uppercase', marginBottom:6 }}>
                Anchor Presentation
              </motion.div>
              <motion.div
                initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
                transition={{ delay:0.45, duration:0.45 }}
                style={{ fontSize:21, fontWeight:900, color:'#1E1B4B',
                  letterSpacing:'-0.5px', lineHeight:1.15, textTransform:'uppercase' }}>
                PHOTOSYNTHESIS
              </motion.div>
            </div>

            {lineIn && (
              <motion.div
                initial={{ width:0 }} animate={{ width:'42%' }}
                transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}
                style={{ height:2.5,
                  background:'linear-gradient(90deg,transparent,#F59E0B,transparent)',
                  borderRadius:2, zIndex:1 }}
              />
            )}

            {metaIn && (
              <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
                style={{ fontSize:9, color:'#6b7280', fontWeight:600, zIndex:1 }}>
                Science · Class 8 · Chapter 4
              </motion.div>
            )}

            {badgesIn && (
              <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
                style={{ display:'flex', flexWrap:'wrap', gap:5,
                  justifyContent:'center', zIndex:1 }}>
                {['6 Slides','Speaker-Ready','Hook Included','Quiz Built-In'].map((b,i) => (
                  <motion.span key={b}
                    initial={{ opacity:0, scale:0.7 }} animate={{ opacity:1, scale:1 }}
                    transition={{ delay:i*0.06, type:'spring', stiffness:360, damping:18 }}
                    style={{ fontSize:6.5, fontWeight:700, color:'#1E1B4B',
                      background:'rgba(30,27,75,0.07)',
                      border:'1px solid rgba(30,27,75,0.18)',
                      padding:'2.5px 7px', borderRadius:99 }}>
                    {b}
                  </motion.span>
                ))}
              </motion.div>
            )}
          </div>
          <SlideFooterBar text="Classroom Projection Ready"/>
        </MockSlide>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 2 — HOOK SLIDE
═══════════════════════════════════════════════════════════════════════════ */
const HOOK = 'Every breath you take depends on a process you can watch happen on a windowsill.';
const HOOK_FACTS = [
  { icon:'🌿', t:'Converts light into stored chemical energy' },
  { icon:'🎨', t:'Chlorophyll reflects green, absorbs red & blue' },
  { icon:'💨', t:'Releases the oxygen every animal breathes' },
];

const PhaseHook = ({ onDone = () => {} }) => {
  const hook = useTypewriter(HOOK, { startDelay:200, charInterval:13 });
  const [factsIn, setFactsIn] = useState([]);

  useEffect(() => {
    if (!hook.done) return;
    HOOK_FACTS.forEach((_,i) => setTimeout(() => setFactsIn(p=>[...p,i]), 250+i*260));
    const factsEnd = 250+HOOK_FACTS.length*260;
    const t = setTimeout(onDone, factsEnd+700);
    return () => clearTimeout(t);
  }, [hook.done, onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ opacity:0, y:30, rotateX:10 }} animate={{ opacity:1, y:0, rotateX:0 }}
        transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}>
        <MockSlide>
          <SlideHeaderBar label="⚡ Buckle Up" slideNum="2"/>
          <div style={{ flex:1, padding:'14px 22px', display:'flex',
            flexDirection:'column', justifyContent:'center', gap:14 }}>

            <div style={{ background:'linear-gradient(135deg,#fef9ec,#fff8e1)',
              border:'1.5px solid #F59E0B', borderRadius:10,
              padding:'12px 16px', fontSize:11, color:'#374151',
              lineHeight:1.5, fontStyle:'italic', fontWeight:600, textAlign:'center' }}>
              "{hook.displayedText}
              {!hook.done && <BlinkingCursor color="#d97706"/>}"
            </div>

            <div style={{ display:'flex', gap:8 }}>
              {HOOK_FACTS.map((f,i) => factsIn.includes(i) && (
                <motion.div key={i} initial={{ opacity:0, y:10, scale:0.9 }}
                  animate={{ opacity:1, y:0, scale:1 }}
                  transition={{ type:'spring', stiffness:320, damping:18 }}
                  style={{ flex:1, background:'#f9fafb', border:'1px solid #e5e7eb',
                    borderRadius:9, padding:'8px 9px', textAlign:'center' }}>
                  <div style={{ fontSize:15, marginBottom:4 }}>{f.icon}</div>
                  <div style={{ fontSize:6.8, color:'#374151', lineHeight:1.4, fontWeight:600 }}>
                    {f.t}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <SlideFooterBar text="Opening Hook"/>
        </MockSlide>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 3 — OBJECTIVES SLIDE
═══════════════════════════════════════════════════════════════════════════ */
const OBJECTIVES = [
  { t:'Define photosynthesis and where it occurs in the cell', rbt:'Remember',   c:'#1e40af' },
  { t:'Describe the role of chlorophyll in light absorption',  rbt:'Understand', c:'#4338ca' },
  { t:'Explain the two stages: Light Reactions & Calvin Cycle', rbt:'Understand', c:'#4338ca' },
  { t:'Compare C3, C4 and CAM pathways across climates',        rbt:'Analyze',    c:'#be185d' },
];

const PhaseObjectives = ({ onDone = () => {} }) => {
  const [objsIn, setObjsIn] = useState([]);
  useEffect(() => {
    OBJECTIVES.forEach((_,i) => setTimeout(() => setObjsIn(p=>[...p,i]), 220+i*280));
    const t = setTimeout(onDone, 220+OBJECTIVES.length*280+650);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ opacity:0, x:40, rotateY:-20 }} animate={{ opacity:1, x:0, rotateY:0 }}
        transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}>
        <MockSlide>
          <SlideHeaderBar label="Learning Objectives" slideNum="3"/>
          <div style={{ flex:1, padding:'14px 20px', display:'flex',
            flexDirection:'column', gap:9, justifyContent:'center' }}>
            {OBJECTIVES.map((obj,i) => objsIn.includes(i) && (
              <motion.div key={i} initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }}
                transition={{ duration:0.32, ease:[0.22,1,0.36,1] }}
                style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                <div style={{ width:15, height:15, borderRadius:5, flexShrink:0,
                  background:'#1E1B4B', marginTop:1,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Check size={8} color="#F59E0B" strokeWidth={3.5}/>
                </div>
                <span style={{ flex:1, fontSize:9, color:'#374151', lineHeight:1.45,
                  fontWeight:600 }}>{obj.t}</span>
                <span style={{ fontSize:6.5, fontWeight:700, color:obj.c, background:`${obj.c}14`,
                  padding:'1px 6px', borderRadius:99, flexShrink:0, marginTop:1.5 }}>{obj.rbt}</span>
              </motion.div>
            ))}
          </div>
          <SlideFooterBar text="Slide 3 of 6"/>
        </MockSlide>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 4 — AGENDA / CONTENT SLIDE
═══════════════════════════════════════════════════════════════════════════ */
const AGENDA = [
  { num:1, title:'Introduction to Photosynthesis', color:'#1E1B4B' },
  { num:2, title:'The Chloroplast',                 color:'#4c1d95' },
  { num:3, title:'Light Reactions',                 color:'#d97706' },
  { num:4, title:'Calvin Cycle (Dark Reaction)',     color:'#0891b2' },
  { num:5, title:'Factors Affecting Rate',           color:'#059669' },
];

const PhaseAgenda = ({ onDone = () => {} }) => {
  const [rowsIn, setRowsIn] = useState([]);
  useEffect(() => {
    AGENDA.forEach((_,i) => setTimeout(() => setRowsIn(p=>[...p,i]), 150+i*210));
    const t = setTimeout(onDone, 150+AGENDA.length*210+650);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ opacity:0, y:-30, scale:0.94 }} animate={{ opacity:1, y:0, scale:1 }}
        transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}>
        <MockSlide>
          <SlideHeaderBar label="📖 Agenda" slideNum="4"/>
          <div style={{ flex:1, padding:'9px 22px', display:'flex',
            flexDirection:'column', gap:5, justifyContent:'center' }}>
            {AGENDA.map((sec,i) => rowsIn.includes(i) && (
              <motion.div key={i}
                initial={{ opacity:0, x:-14 }} animate={{ opacity:1, x:0 }}
                transition={{ duration:0.32, ease:[0.22,1,0.36,1] }}
                style={{ display:'flex', alignItems:'center', gap:9,
                  padding:'4px 10px', borderRadius:7,
                  background:`${sec.color}07`, border:`1.5px solid ${sec.color}25` }}>
                <div style={{ width:17, height:17, borderRadius:5, flexShrink:0,
                  background:sec.color,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <span style={{ color:'#fff', fontSize:8, fontWeight:900 }}>{sec.num}</span>
                </div>
                <span style={{ fontSize:9, fontWeight:700, color:'#1E1B4B' }}>{sec.title}</span>
              </motion.div>
            ))}
          </div>
          <SlideFooterBar text="Slide 4 of 6"/>
        </MockSlide>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 5 — QUIZ SLIDE (click-to-reveal, presenting style)
═══════════════════════════════════════════════════════════════════════════ */
const QUIZ_Q = 'Which organelle is the primary site of photosynthesis?';
const QUIZ_OPTS = { A:'Mitochondria', B:'Chloroplast', C:'Nucleus', D:'Ribosome' };

const PhaseQuiz = ({ onDone = () => {} }) => {
  const [qIn, setQIn] = useState(false);
  const [optsIn, setOptsIn] = useState([]);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setQIn(true), 250);
    const optKeys = Object.keys(QUIZ_OPTS);
    optKeys.forEach((_,i) => setTimeout(() => setOptsIn(p=>[...p,i]), 650+i*180));
    const optsEnd = 650+optKeys.length*180;
    const t2 = setTimeout(() => setRevealed(true), optsEnd+450);
    const t3 = setTimeout(onDone, optsEnd+1300);
    return () => [t1,t2,t3].forEach(clearTimeout);
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',alignItems:'center',
      justifyContent:'center',padding:'10px 12px' }}>
      <motion.div
        initial={{ opacity:0, rotateY:25, x:-30 }} animate={{ opacity:1, rotateY:0, x:0 }}
        transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}>
        <MockSlide>
          <SlideHeaderBar label="🎯 Quick Check" slideNum="5"/>
          <div style={{ flex:1, padding:'13px 20px', display:'flex',
            flexDirection:'column', gap:9, justifyContent:'center' }}>
            {qIn && (
              <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
                transition={{ duration:0.32 }}
                style={{ fontSize:10.5, color:'#111827', lineHeight:1.4, fontWeight:800,
                  textAlign:'center', marginBottom:2 }}>
                {QUIZ_Q}
              </motion.div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
              {Object.entries(QUIZ_OPTS).map(([k,v],i) => optsIn.includes(i) && (
                <motion.div key={k} initial={{ opacity:0, scale:0.9 }} animate={{ opacity:1, scale:1 }}
                  transition={{ duration:0.26 }}
                  style={{ display:'flex', gap:7, alignItems:'center',
                    background: revealed && k==='B' ? '#ecfdf5' : '#f9fafb',
                    border: revealed && k==='B' ? '1.5px solid #6ee7b7' : '1px solid #e5e7eb',
                    borderRadius:7, padding:'6px 9px' }}>
                  <span style={{ background: revealed && k==='B' ? '#059669' : '#1e3a8a', color:'#fff',
                    width:15, height:15, borderRadius:'50%', display:'flex',
                    alignItems:'center', justifyContent:'center', fontSize:7,
                    fontWeight:700, flexShrink:0 }}>{k}</span>
                  <span style={{ fontSize:8, color:'#374151', fontWeight:600 }}>{v}</span>
                  {revealed && k==='B' && <Check size={11} color="#059669" strokeWidth={3} style={{ marginLeft:'auto' }}/>}
                </motion.div>
              ))}
            </div>
          </div>
          <SlideFooterBar text="Click to Reveal — Built In"/>
        </MockSlide>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PHASE 6 — PRESENT FINALE (deck fans → collapses → projected to class)
═══════════════════════════════════════════════════════════════════════════ */
const StudentSilhouette = ({ color='#94a3b8', size=36 }) => (
  <svg viewBox="0 0 40 48" width={size} height={size*1.2}>
    <circle cx="20" cy="12" r="10" fill={color}/>
    <path d="M2 46 C2 30 8 24 20 24 C32 24 38 30 38 46 Z" fill={color}/>
  </svg>
);

const MiniSlide = ({ hColor, lineColors }) => (
  <div style={{ position:'absolute', inset:0, borderRadius:8, overflow:'hidden',
    background:'#fff', display:'flex', flexDirection:'column' }}>
    <div style={{ height:14, background:hColor,
      display:'flex', alignItems:'center', paddingLeft:7, gap:4, flexShrink:0 }}>
      <div style={{ width:6,height:6,borderRadius:'50%',background:'rgba(255,255,255,0.5)' }}/>
      <div style={{ width:38,height:3,background:'rgba(255,255,255,0.6)',borderRadius:2 }}/>
    </div>
    <div style={{ height:1.5, background:'#F59E0B', opacity:0.65, flexShrink:0 }}/>
    <div style={{ flex:1, padding:'7px 9px', display:'flex', flexDirection:'column',
      gap:4, justifyContent:'center' }}>
      {lineColors.map((c,i) => (
        <div key={i} style={{ height:4, borderRadius:2, background:c,
          width:`${85-i*14}%`, opacity:0.55+i*0.06 }}/>
      ))}
    </div>
  </div>
);

const SLIDE_FAN = [
  { rotate:-14, x:-32, y:8,  hColor:'#1E1B4B', lines:['rgba(30,27,75,0.28)','rgba(30,27,75,0.16)'] },
  { rotate:-6,  x:-13, y:-3, hColor:'#d97706', lines:['rgba(217,119,6,0.32)','rgba(30,27,75,0.14)'] },
  { rotate:0,   x:0,   y:-9, hColor:'#1E1B4B', lines:['rgba(245,158,11,0.30)','rgba(30,27,75,0.14)'] },
  { rotate:6,   x:13,  y:-3, hColor:'#0891b2', lines:['rgba(8,145,178,0.30)','rgba(30,27,75,0.14)'] },
  { rotate:14,  x:32,  y:8,  hColor:'#7c3aed', lines:['rgba(124,58,237,0.30)','rgba(30,27,75,0.14)'] },
];
const SILHOUETTES = [
  { color:'#94a3b8', delay:0    },
  { color:'#a8a29e', delay:0.12 },
  { color:'#b0c4de', delay:0.22 },
];
const PAYOFF_CHIPS = [
  { label:'6 Slides',        color:'#1E1B4B' },
  { label:'Project-Ready',   color:'#F59E0B' },
  { label:'Editable Anytime',color:'#7c3aed' },
];

const PhasePresent = ({ onDone = () => {} }) => {
  const [fanIn,       setFanIn]       = useState(false);
  const [collapsing,  setCollapsing]  = useState(false);
  const [screenIn,    setScreenIn]    = useState(false);
  const [screenMoved, setScreenMoved] = useState(false);
  const [beamIn,      setBeamIn]      = useState(false);
  const [silhouettesIn, setSilhouettesIn] = useState(false);
  const [chipsIn,     setChipsIn]     = useState([]);
  const [captionIn,   setCaptionIn]   = useState(false);

  useEffect(() => {
    const timers = [
      setTimeout(() => setFanIn(true),         100),
      setTimeout(() => setCollapsing(true),   1300),
      setTimeout(() => setScreenIn(true),      1950),
      setTimeout(() => setScreenMoved(true),   2750),
      setTimeout(() => setBeamIn(true),        3000),
      setTimeout(() => setSilhouettesIn(true), 3250),
      setTimeout(() => setChipsIn([0]),        3650),
      setTimeout(() => setChipsIn(p=>[...p,1]),3800),
      setTimeout(() => setChipsIn(p=>[...p,2]),3950),
      setTimeout(() => setCaptionIn(true),     4250),
      setTimeout(onDone,                       4950),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onDone]);

  return (
    <div style={{ width:'100%',height:'100%',display:'flex',flexDirection:'column',
      alignItems:'center',justifyContent:'center',padding:'10px 14px',gap:12,
      position:'relative' }}>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
        gap: screenMoved ? 50 : 0,
        transition:'gap 0.65s cubic-bezier(0.22,1,0.36,1)', minHeight:330 }}>

        {/* Fan of slides → single presentation screen */}
        <div style={{ position:'relative', width:336, height:198 }}>
          {screenIn && (
            <motion.div
              animate={{ opacity:[0.35,0.65,0.35], scale:[1,1.08,1] }}
              transition={{ duration:2.2, repeat:Infinity, ease:'easeInOut' }}
              style={{ position:'absolute', inset:-32, borderRadius:34, zIndex:-1,
                background:'radial-gradient(circle, rgba(245,158,11,0.30), rgba(124,58,237,0.18), transparent 70%)',
                filter:'blur(24px)' }}
            />
          )}

          <AnimatePresence>
            {!screenIn && fanIn && SLIDE_FAN.map((s,i) => (
              <motion.div key={i}
                initial={{ opacity:0, scale:0.5, rotate:0, x:0, y:34 }}
                animate={{
                  opacity:1, scale:1,
                  rotate: collapsing ? 0 : s.rotate,
                  x:      collapsing ? 0 : s.x * 1.45,
                  y:      collapsing ? 0 : s.y * 1.45,
                }}
                exit={{ opacity:0, scale:0.88, transition:{ duration:0.26 } }}
                transition={{
                  delay: collapsing ? 0 : 0.07+i*0.08,
                  duration:0.5, ease:[0.22,1,0.36,1],
                }}
                style={{ position:'absolute', left:'50%', top:'50%', width:220, height:123,
                  marginLeft:-110, marginTop:-61,
                  border:'2px solid rgba(30,27,75,0.45)', borderRadius:10,
                  boxShadow:'0 18px 42px rgba(0,0,0,0.30)',
                  transformOrigin:'center bottom' }}>
                <MiniSlide hColor={s.hColor} lineColors={s.lines}/>
              </motion.div>
            ))}
          </AnimatePresence>

          <AnimatePresence>
            {screenIn && (
              <motion.div
                initial={{ opacity:0, scale:0.7, y:24 }}
                animate={{ opacity:1, scale: screenMoved ? 0.92 : 1, y:0 }}
                transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}
                style={{ position:'absolute', inset:0, background:'#0b1020',
                  borderRadius:14, border:'5px solid #1E1B4B', overflow:'hidden',
                  boxShadow:'0 32px 76px rgba(0,0,0,0.45), 0 0 0 1px rgba(245,158,11,0.25)' }}>
                {beamIn && (
                  <motion.div
                    initial={{ opacity:0 }} animate={{ opacity:[0,0.5,0.3] }}
                    transition={{ duration:1.2, ease:'easeOut' }}
                    style={{ position:'absolute', inset:0,
                      background:'radial-gradient(ellipse at 50% 0%, rgba(245,158,11,0.35), transparent 65%)' }}
                  />
                )}
                <div style={{ position:'absolute', inset:9, borderRadius:9,
                  background:'#fff', display:'flex', flexDirection:'column', overflow:'hidden' }}>
                  <div style={{ height:23, background:'#1E1B4B',
                    display:'flex', alignItems:'center', paddingLeft:12, gap:7 }}>
                    <picture>
                      <source srcSet="/TechGenieMascot.webp" type="image/webp" />
                      <img src="/TechGenieMascot.png" alt=""
                        style={{ width:13,height:13,objectFit:'contain' }}/>
                    </picture>
                    <span style={{ fontSize:8.5, fontWeight:800, color:'white' }}>TeachGenie.ai</span>
                  </div>
                  <div style={{ height:2, background:'#F59E0B', opacity:0.7 }}/>
                  <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center',
                    flexDirection:'column', gap:5, padding:'0 16px', textAlign:'center' }}>
                    <div style={{ fontSize:12, fontWeight:900, color:'#F59E0B',
                      letterSpacing:'0.4px' }}>
                      PHOTOSYNTHESIS
                    </div>
                    <div style={{ fontSize:7.5, fontWeight:700, color:'#1E1B4B', opacity:0.75 }}>
                      Light Reactions → Calvin Cycle
                    </div>
                    <div style={{ fontSize:6.5, fontWeight:600, color:'#1E1B4B', opacity:0.45 }}>
                      6CO₂ + 6H₂O + light → C₆H₁₂O₆ + 6O₂
                    </div>
                  </div>
                </div>
                {/* stand */}
                <div style={{ position:'absolute', bottom:-20, left:'50%', marginLeft:-4,
                  width:8, height:20, background:'#1E1B4B' }}/>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {silhouettesIn && (
            <motion.div
              initial={{ opacity:0, x:30 }} animate={{ opacity:1, x:0 }}
              transition={{ duration:0.46, ease:[0.22,1,0.36,1] }}
              style={{ display:'flex', alignItems:'flex-end', gap:20 }}>
              {SILHOUETTES.map((s,i) => (
                <motion.div key={i}
                  initial={{ opacity:0, y:16, scale:0.72 }}
                  animate={{ opacity:1, y:0, scale:1 }}
                  transition={{ delay:s.delay, type:'spring', stiffness:300, damping:18 }}>
                  <StudentSilhouette color={s.color} size={58+i*7}/>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div style={{ display:'flex', gap:9, justifyContent:'center', minHeight:24 }}>
        {PAYOFF_CHIPS.map((c,i) => chipsIn.includes(i) && (
          <motion.span key={c.label}
            initial={{ opacity:0, y:8, scale:0.8 }} animate={{ opacity:1, y:0, scale:1 }}
            transition={{ type:'spring', stiffness:340, damping:18 }}
            style={{ fontSize:11, fontWeight:800, color:'#fff',
              background:c.color, padding:'5px 13px', borderRadius:99,
              boxShadow:`0 4px 14px ${c.color}55` }}>
            ✓ {c.label}
          </motion.span>
        ))}
      </div>

      <AnimatePresence>
        {captionIn && (
          <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
            transition={{ duration:0.4 }}
            style={{ textAlign:'center' }}>
            <div style={{ fontSize:19, fontWeight:800,
              color:'rgba(255,255,255,0.92)', letterSpacing:'-0.2px' }}>
              One deck. <span style={{ color:'#F59E0B' }}>Every</span> classroom.
            </div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.42)',
              marginTop:6, fontStyle:'italic' }}>
              Project instantly. Edit anytime.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Phases ──────────────────────────────────────────────────────────────── */
const PHASES = [
  { id:'cover',   label:'Title',   Component:PhaseCover      },
  { id:'hook',    label:'Hook',    Component:PhaseHook       },
  { id:'objs',    label:'Goals',   Component:PhaseObjectives },
  { id:'agenda',  label:'Agenda',  Component:PhaseAgenda      },
  { id:'quiz',    label:'Quiz',    Component:PhaseQuiz        },
  { id:'present', label:'Present', Component:PhasePresent     },
];

/* ── Left panel data ─────────────────────────────────────────────────────── */
const PPT_FEATS = [
  'Auto-generated, classroom-ready slide deck for every lesson',
  'Cover, hook, objectives, agenda & quiz slides — fully formatted',
  'Project directly or hand off to a co-teacher — no extra editing needed',
];

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN MODAL
═══════════════════════════════════════════════════════════════════════════ */
const PPTUpgradeModal = ({ isOpen, onClose, currentTier = 'free' }) => {
  const navigate = useNavigate();
  const [phase,    setPhase]    = useState(0);
  const [barReady, setBarReady] = useState(false);
  const [mobile, setMobile] = useState(() => window.innerWidth < 700);

  // PPT is included from Free onward — the trigger is always "you used your cap",
  // so the only question is which tier(s) to offer next.
  const planOptions = useMemo(() => getUpgradeOptions(currentTier), [currentTier]);
  const onlyInstitutional = planOptions.length === 1 && planOptions[0].key === 'institutional';

  const [selPlan, setSelPlan] = useState(planOptions[0]?.key || 'silver');
  useEffect(() => { setSelPlan(planOptions[0]?.key || 'silver'); }, [planOptions]);

  const advanceRef = useRef(null);
  const safetyRef  = useRef(null);

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 700);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const goNext = useCallback(() => {
    setPhase(p => (p+1) % PHASES.length);
  }, []);

  const handlePhaseDone = useCallback(() => {
    setBarReady(true);
    clearTimeout(advanceRef.current);
    advanceRef.current = setTimeout(goNext, ADVANCE_BUFFER_MS);
  }, [goNext]);

  useEffect(() => {
    if (!isOpen) return;
    setBarReady(false);
    clearTimeout(safetyRef.current);
    safetyRef.current = setTimeout(goNext, SAFETY_CAP_MS);
    return () => {
      clearTimeout(advanceRef.current);
      clearTimeout(safetyRef.current);
    };
  }, [isOpen, phase, goNext]);

  useEffect(() => { if (isOpen) setPhase(0); }, [isOpen]);

  const handleNavClick = (i) => {
    clearTimeout(advanceRef.current);
    clearTimeout(safetyRef.current);
    setPhase(i);
  };

  const { Component: PhaseComp } = PHASES[phase];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={onClose}
            style={{ position:'fixed', inset:0, background:'rgba(4,2,18,0.78)',
              backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', zIndex:9998 }}
          />

          <motion.div
            style={{ position:'fixed', inset:0, display:'flex', alignItems:'center',
              justifyContent:'center', pointerEvents:'none', zIndex:9999, padding:'16px' }}
            initial={{ opacity:0, scale:0.87, y:52 }}
            animate={{ opacity:1, scale:1, y:0 }}
            exit={{ opacity:0, scale:0.87, y:52 }}
            transition={{ duration:0.5, ease:[0.34,1.56,0.64,1] }}>

            <div style={{ pointerEvents:'auto', maxWidth:1100, width:'100%',
              maxHeight:'calc(100vh - 32px)', borderRadius:26, overflow:'hidden',
              boxShadow:'0 80px 200px -20px rgba(0,0,0,0.88), 0 0 0 1px rgba(255,255,255,0.06)',
              display:'flex', flexDirection:mobile?'column':'row', position:'relative' }}>

              <button onClick={onClose}
                style={{ position:'absolute', top:14, right:14,
                  background:'rgba(0,0,0,0.55)', backdropFilter:'blur(10px)',
                  border:'1px solid rgba(255,255,255,0.14)', color:'rgba(255,255,255,0.7)',
                  width:30, height:30, borderRadius:'50%',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  cursor:'pointer', zIndex:30, transition:'all 0.18s' }}
                onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.16)'; e.currentTarget.style.color='#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background='rgba(0,0,0,0.55)'; e.currentTarget.style.color='rgba(255,255,255,0.7)'; }}>
                <X size={12}/>
              </button>

              {/* ── LEFT PANEL ───────────────────────────────────────────── */}
              <div style={{ width:mobile?'100%':284, flexShrink:0, background:'#fff',
                padding:'28px 22px 26px', display:'flex', flexDirection:'column',
                overflowY:'auto', order:mobile?2:1 }}>

                <motion.div
                  initial={{ scale:0, rotate:-18 }} animate={{ scale:1, rotate:0 }}
                  transition={{ type:'spring', stiffness:220, delay:0.1 }}
                  style={{ width:52, height:52, borderRadius:14, marginBottom:16,
                    background:'linear-gradient(135deg,#1E1B4B,#4c1d95)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow:'0 8px 24px rgba(30,27,75,0.3)' }}>
                  <MonitorPlay size={22} color="#F59E0B"/>
                </motion.div>

                <motion.h2 initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
                  transition={{ delay:0.15 }}
                  style={{ fontSize:18, fontWeight:900, color:'#111827', marginBottom:6,
                    lineHeight:1.26, letterSpacing:'-0.3px' }}>
                  You've Used Your PPT Limit
                </motion.h2>

                <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }}
                  transition={{ delay:0.22 }}
                  style={{ fontSize:11.5, color:'#6b7280', lineHeight:1.6,
                    marginBottom:18, fontWeight:400 }}>
                  You've exported Anchor PPTs for every lesson your plan allows. Upgrade for a higher
                  lesson cap{onlyInstitutional ? ', or go Institutional for unlimited PPT exports.' : '.'}
                </motion.p>

                <div style={{ display:'flex', flexDirection:'column', gap:9, marginBottom:18 }}>
                  {PPT_FEATS.map((f,i) => (
                    <motion.div key={f} initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }}
                      transition={{ delay:0.26+i*0.07 }}
                      style={{ display:'flex', alignItems:'flex-start', gap:9 }}>
                      <div style={{ width:16, height:16, borderRadius:'50%', flexShrink:0,
                        marginTop:1.5,
                        background:'linear-gradient(135deg,#1E1B4B,#4c1d95)',
                        display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <Check size={8} color="#fff" strokeWidth={3}/>
                      </div>
                      <span style={{ fontSize:11.5, color:'#6b7280', lineHeight:1.5,
                        fontWeight:400 }}>{f}</span>
                    </motion.div>
                  ))}
                </div>

                <div style={{ height:1, background:'#f0f0f0', marginBottom:14 }}/>
                <p style={{ fontSize:9, fontWeight:700, color:'#9ca3af',
                  textTransform:'uppercase', letterSpacing:'1.4px', marginBottom:9 }}>
                  Select Plan
                </p>

                <div style={{ display:'flex', flexDirection:'column', gap:7, marginBottom:20 }}>
                  {planOptions.map((plan, idx) => {
                    const sel = selPlan === plan.key;
                    const isRecommended = idx === 0 && !onlyInstitutional;
                    return (
                      <motion.div key={plan.key}
                        onClick={() => setSelPlan(plan.key)}
                        whileHover={{ y:-1 }} whileTap={{ scale:0.99 }}
                        style={{ padding:'8px 11px', borderRadius:10, cursor:'pointer',
                          border:`1.5px solid ${sel ? plan.dot : '#e5e7eb'}`,
                          background: sel ? `${plan.dot}08` : '#fafafa',
                          transition:'border 0.14s,background 0.14s',
                          position:'relative' }}>
                        {plan.popular && (
                          <span style={{ position:'absolute', top:-7, right:10, color:'#fff',
                            fontSize:7, fontWeight:700, padding:'1px 7px', borderRadius:99,
                            textTransform:'uppercase', background:'#f59e0b' }}>
                            Popular
                          </span>
                        )}
                        {isRecommended && !plan.popular && (
                          <span style={{ position:'absolute', top:-7, right:10, color:'#fff',
                            fontSize:7, fontWeight:700, padding:'1px 7px', borderRadius:99,
                            textTransform:'uppercase', background:'#1E1B4B' }}>
                            Recommended
                          </span>
                        )}
                        <div style={{ display:'flex', justifyContent:'space-between',
                          alignItems:'center', marginBottom:4 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <div style={{ width:7, height:7, borderRadius:'50%',
                              background:plan.dot,
                              boxShadow:sel?`0 0 6px ${plan.dot}`:'none' }}/>
                            <span style={{ fontSize:12.5, fontWeight:700, color:'#111827' }}>
                              {plan.name}
                            </span>
                          </div>
                          <div style={{ display:'flex', alignItems:'baseline', gap:3 }}>
                            <span style={{ fontSize:13, fontWeight:800, color:'#111827' }}>
                              {plan.price}
                            </span>
                            {plan.original && (
                              <s style={{ fontSize:9, color:'#d1d5db', fontWeight:500 }}>
                                {plan.original}
                              </s>
                            )}
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:6 }}>
                          <span style={{ fontSize:8, fontWeight:600, color:plan.dot,
                            background:`${plan.dot}14`, borderRadius:99, padding:'1px 6px' }}>
                            PPT: {formatCapShort(plan.ppt)}
                          </span>
                          <span style={{ fontSize:8, fontWeight:600, color:plan.dot,
                            background:`${plan.dot}14`, borderRadius:99, padding:'1px 6px' }}>
                            PDF: {formatCapShort(plan.pdf)}
                          </span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                <motion.button
                  onClick={() => {
                    onClose();
                    if (selPlan === 'institutional') navigate('/contact');
                    else navigate(`/pricing?plan=${selPlan}`);
                  }}
                  whileHover={{ y:-2, boxShadow:'0 16px 36px rgba(30,27,75,0.42)' }}
                  whileTap={{ scale:0.98 }}
                  style={{ width:'100%', height:46, borderRadius:50, border:'none',
                    background:'linear-gradient(135deg,#1E1B4B,#4c1d95)',
                    color:'#F59E0B', fontSize:13.5, fontWeight:800, cursor:'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center', gap:7,
                    boxShadow:'0 6px 20px rgba(30,27,75,0.24)', marginBottom:9,
                    letterSpacing:'0.2px' }}>
                  <Zap size={13} fill="#F59E0B" strokeWidth={0}/>
                  {selPlan === 'institutional' ? 'Contact Sales' : 'Upgrade for More PPT Exports'}
                </motion.button>

                <button onClick={onClose}
                  style={{ width:'100%', height:36, borderRadius:50, background:'transparent',
                    border:'none', color:'#9ca3af', fontSize:12, fontWeight:500,
                    cursor:'pointer', transition:'color 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.color='#6b7280'; }}
                  onMouseLeave={e => { e.currentTarget.style.color='#9ca3af'; }}>
                  Maybe Later
                </button>
              </div>

              {/* ── RIGHT PANEL ──────────────────────────────────────────── */}
              <div style={{ flex:1, background:'#060916',
                display:'flex', flexDirection:'column',
                minHeight:mobile?580:660,
                borderRadius:mobile?'24px 24px 0 0':'0 26px 26px 0',
                overflow:'hidden', position:'relative', order:mobile?1:2 }}>

                <BackgroundOrbs/>

                <motion.div key={`pb-${phase}`}
                  initial={{ width:'0%' }}
                  animate={{ width: barReady ? '100%' : '88%' }}
                  transition={{ duration: barReady ? 0.3 : 5.5,
                    ease: barReady ? 'easeOut' : 'linear' }}
                  style={{ height:2.5, flexShrink:0, zIndex:10, position:'relative',
                    background:'linear-gradient(90deg,#1E1B4B,#F59E0B)',
                    boxShadow:'0 0 10px rgba(245,158,11,0.65)' }}
                />

                <div style={{ flex:1, position:'relative', overflow:'hidden',
                  zIndex:1, perspective:1200 }}>
                  <AnimatePresence mode="wait">
                    <motion.div key={phase}
                      initial={{ opacity:0, rotateY:-42, x:55, scale:0.94 }}
                      animate={{ opacity:1, rotateY:0, x:0, scale:1 }}
                      exit={{ opacity:0, rotateY:42, x:-55, scale:0.94,
                        transition:{ duration:0.28 } }}
                      transition={{ duration:0.5, ease:[0.22,1,0.36,1] }}
                      style={{ position:'absolute', inset:0, transformStyle:'preserve-3d' }}>
                      <PhaseComp onDone={handlePhaseDone}/>
                    </motion.div>
                  </AnimatePresence>
                </div>

                <div style={{ height:54, flexShrink:0, position:'relative', zIndex:10,
                  background:'rgba(6,9,22,0.97)',
                  borderTop:'0.5px solid rgba(255,255,255,0.07)',
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {PHASES.map((step,i) => (
                    <React.Fragment key={step.id}>
                      <button onClick={() => handleNavClick(i)}
                        style={{ background:'none', border:'none', cursor:'pointer',
                          outline:'none', display:'flex', flexDirection:'column',
                          alignItems:'center', gap:3, padding:'0 5px' }}>
                        <div style={{ position:'relative', width:16, height:16,
                          display:'flex', alignItems:'center', justifyContent:'center' }}>
                          {i === phase && (
                            <motion.div
                              animate={{ scale:[1,2.4,1], opacity:[0.35,0,0.35] }}
                              transition={{ duration:1.6, repeat:Infinity, ease:'easeOut' }}
                              style={{ position:'absolute', width:16, height:16,
                                borderRadius:'50%', border:'1px solid #F59E0B',
                                pointerEvents:'none' }}
                            />
                          )}
                          <motion.div
                            animate={{
                              width:      i===phase ? 9 : 5.5,
                              height:     i===phase ? 9 : 5.5,
                              background: i===phase ? '#F59E0B'
                                : i<phase ? 'rgba(245,158,11,0.45)'
                                : 'rgba(255,255,255,0.15)',
                              boxShadow:  i===phase ? '0 0 8px rgba(245,158,11,0.65)' : 'none',
                            }}
                            transition={{ duration:0.2 }}
                            style={{ borderRadius:'50%' }}
                          />
                        </div>
                        <span style={{ fontSize:7.5, whiteSpace:'nowrap',
                          color:i===phase ? 'rgba(255,255,255,0.86)' : 'rgba(255,255,255,0.24)',
                          fontWeight:i===phase ? 700 : 400, transition:'color 0.22s' }}>
                          {step.label}
                        </span>
                      </button>
                      {i < PHASES.length-1 && (
                        <motion.div
                          animate={{ background: i<phase
                            ? 'rgba(245,158,11,0.45)'
                            : 'rgba(255,255,255,0.07)' }}
                          transition={{ duration:0.4 }}
                          style={{ width:18, height:1, flexShrink:0, marginBottom:12 }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PPTUpgradeModal;
